import React, { useEffect, useState } from 'react';
import { Button, Box, Typography, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
  Button as FuelButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalContentText,
  ModalTitle,
} from '@deere/fuel-react';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import ManualRequestTable from './ManualRequestTable';
import NewRequestModal from './NewRequestModal';
import LineItemSelectionModal from './LineItemSelectionModal';
import FinalManualRequestModal from './FinalManualRequestModal';
import { getDataWAuth, postDataWAuth, deleteDataWAuth, putDataWAuth } from '../../utils/api-service';
import { ADD_PRODUCT_TYPE, MANUAL_REQUEST_ACTION_ROLES, MANUAL_REQUEST_CONFIRM, MANUAL_REQUEST_LIST, MANUAL_REQUEST_OPEN_ITEMS, MANUAL_REQUEST_SAVE, MANUAL_REQUEST_DELETE, MANUAL_REQUEST_VIEW_ROLES } from '../../constants/constant';
import ToastMessage from '../../common/toast-message';
import { getUserRole } from '../../utils/utils';

const ManualRequest = () => {
  const userData = useSelector((state) => state.userState.userData);
  const userRole = getUserRole(userData);
  const canViewManualRequests = MANUAL_REQUEST_VIEW_ROLES.includes(userRole);
  const canManageManualRequests = MANUAL_REQUEST_ACTION_ROLES.includes(userRole);
  const [manualRequests, setManualRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastData, setToastData] = useState({ message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);
  const [lookupItems, setLookupItems] = useState([]);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [finalModalOpen, setFinalModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRequestKey, setNewRequestKey] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [confirmationIds, setConfirmationIds] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Fetch manual requests on component mount
  useEffect(() => {
    if (canViewManualRequests) {
      fetchManualRequests();
    }
  }, [canViewManualRequests]);
  
  const deleteManualRequests = async (ids) => {
    if (!canManageManualRequests) return;
    const idList = Array.isArray(ids) ? ids : [ids];
    try {
      setDeleting(true);
      const response = await deleteDataWAuth(`${MANUAL_REQUEST_DELETE}/${idList.join(',')}`);
      if (response?.status === 200) {
        setSelectedRequestIds([]);
        await fetchManualRequests();
        setToastData({ message: `${idList.length} manual request(s) deleted successfully`, severity: 'success' });
        setToastOpen(true);
        return true;
      }
      throw new Error('Manual request(s) were not deleted');
    } catch (error) {
      console.error('Error deleting manual requests:', error);
      setToastData({ message: error.response?.data?.message || 'Failed to delete manual request(s)', severity: 'error' });
      setToastOpen(true);
      // Rows may have changed status in the meantime (e.g. confirmed by another user).
      if (error.response?.status === 400) {
        await fetchManualRequests();
      }
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelectedClick = () => {
    if (selectedRequestIds.length === 0) {
      setToastData({ message: 'No data selected !!!', severity: 'warning' });
      setToastOpen(true);
      return;
    }
    setBulkDeleteOpen(true);
  };

  const handleBulkDelete = async () => {
    await deleteManualRequests(selectedRequestIds);
    setBulkDeleteOpen(false);
  };

  const fetchManualRequests = async () => {
    try {
      setLoading(true);
      const response = await getDataWAuth(MANUAL_REQUEST_LIST);
      
      if (response && response.status === 200) {
        const responseData = response.data;
        const manualRequestList = Array.isArray(responseData)
          ? responseData
          : responseData?.data || responseData?.extensionRequestList || [];
        const requests = Array.isArray(manualRequestList) ? manualRequestList : [];
        setManualRequests(requests);
        window.dispatchEvent(
          new CustomEvent('manualRequestCountUpdated', {
            detail: requests.filter((request) => request.requestStatus === 'Manual_New').length,
          }),
        );
      } else {
        setManualRequests([]);
        window.dispatchEvent(new CustomEvent('manualRequestCountUpdated', { detail: 0 }));
      }
    } catch (error) {
      console.error('Error fetching manual requests:', error);
      setToastData({ message: 'Failed to fetch manual requests', severity: 'error' });
      setToastOpen(true);
      setManualRequests([]);
      window.dispatchEvent(new CustomEvent('manualRequestCountUpdated', { detail: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = () => {
    if (!canManageManualRequests) return;
    setNewRequestKey((key) => key + 1);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSelectionBack = () => {
    setSelectionModalOpen(false);
    setModalOpen(true);
  };

  const handleSelectionClose = () => {
    setSelectionModalOpen(false);
    setLookupItems([]);
  };

  const handleLineItemNext = (selectedLineItem) => {
    console.log('Manual request selected line item payload:', selectedLineItem);
    setSelectedLineItem(selectedLineItem);
    setSelectionModalOpen(false);
    setFinalModalOpen(true);
  };

  const handleFinalModalBack = () => {
    setFinalModalOpen(false);
    setSelectionModalOpen(true);
  };

  const handleFinalModalClose = () => {
    setFinalModalOpen(false);
    setSelectedLineItem(null);
    setLookupItems([]);
  };

  const handleFinalConfirm = async (savePayload, newProductType) => {
    if (!canManageManualRequests) return;
    console.log('Manual request save payload:', savePayload);
    try {
      setSaving(true);
      if (newProductType) {
        const productTypeResponse = await postDataWAuth(ADD_PRODUCT_TYPE, newProductType);
        if (productTypeResponse?.status !== 200) {
          throw new Error('Base code was not saved');
        }
      }

      const response = await postDataWAuth(MANUAL_REQUEST_SAVE, { ...savePayload, userID: userData?.userID });

      if (response?.status === 200) {
        await fetchManualRequests();
        setToastData({ message: 'Manual request saved successfully', severity: 'success' });
        setToastOpen(true);
        handleFinalModalClose();
        return;
      }

      throw new Error('Manual request was not saved');
    } catch (error) {
      console.error('Error saving manual request:', error);
      setToastData({
        message: error.response?.data?.message || 'Failed to save manual request',
        severity: 'error',
      });
      setToastOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleModalSubmit = async (formData) => {
    if (!canManageManualRequests) return false;
    try {
      setSubmitting(true);
      const lookupPayload = {
        companyCode: formData.companyCode,
        customerNo: `00${formData.customerNumber}`,
        assignmentNo: formData.assignmentNumber,
      };

      console.log('Manual request lookup payload:', lookupPayload);

      const response = await getDataWAuth(MANUAL_REQUEST_OPEN_ITEMS, {
        comapnycode: lookupPayload.companyCode,
        customerNo: lookupPayload.customerNo,
        assignNo: lookupPayload.assignmentNo,
      });
      
      const responseData = response?.data;
      const extensionRequestList = Array.isArray(responseData)
        ? responseData
        : responseData?.data || responseData?.extensionRequestList || [];
      if (response?.status === 200 && extensionRequestList.length > 0) {
        setLookupItems(extensionRequestList);
        setModalOpen(false);
        setSelectionModalOpen(true);
        return true;
      }

      setToastData({ message: 'No matching extension requests found', severity: 'error' });
      setToastOpen(true);
      return false;
    } catch (error) {
      console.error('Error creating manual request:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create manual request';
      setToastData({ message: errorMessage, severity: 'error' });
      setToastOpen(true);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const requestConfirmation = (requestIds) => {
    setConfirmationIds(requestIds);
    setConfirmationOpen(true);
  };

  const handleConfirmSelectedClick = () => {
    if (selectedRequestIds.length === 0) {
      setToastData({ message: 'No data selected !!!', severity: 'warning' });
      setToastOpen(true);
      return;
    }
    requestConfirmation(selectedRequestIds);
  };

  const handleConfirmManualRequests = async () => {
    if (!canManageManualRequests) return;
    try {
      setConfirming(true);
      const response = await putDataWAuth(MANUAL_REQUEST_CONFIRM, { requestIds: confirmationIds });
      if (response?.status !== 200) {
        throw new Error('Manual requests were not confirmed');
      }
      setSelectedRequestIds([]);
      setConfirmationOpen(false);
      await fetchManualRequests();
      setToastData({ message: 'Manual request(s) confirmed for the next scheduler run', severity: 'success' });
      setToastOpen(true);
    } catch (error) {
      setToastData({ message: error.response?.data?.message || 'Failed to confirm manual request(s)', severity: 'error' });
      setToastOpen(true);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box sx={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '4px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h5" sx={{ fontSize: '22px', fontWeight: 600, color: '#222222' }}>
          Manual Request
        </Typography>
        {canManageManualRequests && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewRequest}
            sx={{
              backgroundColor: '#ffd800',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e6c200'
              }
            }}
          >
            New Request
          </Button>
        )}
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <ManualRequestTable
          data={manualRequests} delete={deleteManualRequests} deleting={deleting}
          selectedRequestIds={selectedRequestIds}
          onSelectionChange={setSelectedRequestIds}
          onConfirmRequest={(requestId) => requestConfirmation([requestId])}
          onConfirmSelected={handleConfirmSelectedClick}
          onDeleteSelected={handleDeleteSelectedClick}
          isConfirming={confirming}
          canConfirm={canManageManualRequests}
        />
      )}

      {/* Modal */}
      <NewRequestModal
        key={newRequestKey}
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        isSubmitting={submitting}
      />
      <LineItemSelectionModal
        open={selectionModalOpen}
        items={lookupItems}
        onBack={handleSelectionBack}
        onClose={handleSelectionClose}
        onNext={handleLineItemNext}
      />
      <FinalManualRequestModal
        open={finalModalOpen}
        selectedLineItem={selectedLineItem || {}}
        onBack={handleFinalModalBack}
        onClose={handleFinalModalClose}
        onConfirm={handleFinalConfirm}
        isSaving={saving}
      />
      <Dialog open={confirmationOpen} onClose={() => !confirming && setConfirmationOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm request{confirmationIds.length > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmed requests will be picked up by the next scheduler run. They cannot be edited or cancelled after confirmation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)} disabled={confirming}>Cancel</Button>
          <Button
            onClick={handleConfirmManualRequests}
            variant="contained"
            disabled={confirming}
            sx={{
              backgroundColor: '#ffd800',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e6c200',
              },
            }}
          >
            {confirming ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={bulkDeleteOpen} onClose={() => !deleting && setBulkDeleteOpen(false)}>
        <ModalTitle>Confirm Deletion</ModalTitle>
        <ModalContent>
          <ModalContentText>
            Are you sure you want to delete {selectedRequestIds.length} manual request(s)? This cannot be undone.
          </ModalContentText>
        </ModalContent>
        <ModalActions>
          <FuelButton variant="secondary" onClick={() => setBulkDeleteOpen(false)} disabled={deleting}>Cancel</FuelButton>
          <FuelButton variant="primary" onClick={handleBulkDelete} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </FuelButton>
        </ModalActions>
      </Modal>

      {/* Toast Message */}
      <ToastMessage
        open={toastOpen}
        toastData={toastData}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ManualRequest;
